/**
 * ComponentActionsForm.jsx
 * =========================
 * Component for adding/managing actions on PageBuilder components
 * Uses the new Action Configuration System
 * 
 * Updated: 2025-11-20
 * - Replaced old ActionSystem with new actions folder
 * - Uses ActionConfigPopup for configuration
 * - Stores actions with reference + copy pattern
 */

import React, { useState, useEffect } from 'react';
import { useAppStore } from '../../../store/useAppStore';
import { ActionConfigPopup } from '../../actions';
import { ActionElementRenderer } from '../../actions';
import { executeAction } from '../../../utils/actionExecutor';

const ComponentActionsForm = ({ component, onUpdate }) => {
  const { appName } = useAppStore();
  
  // Local state for actions
  const [actions, setActions] = useState(component.actions || []);
  const [showActionPopup, setShowActionPopup] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);

  // Keep local state in sync with component
  useEffect(() => {
    setActions(component.actions || []);
  }, [component.actions]);

  /**
   * Handle action selection from popup
   * Stores both actionId (reference) and actionJSON (full copy)
   */
  const handleActionSelect = (actionConfig) => {
    const newAction = {
      actionId: actionConfig.actionId || `action_${Date.now()}`,
      actionJSON: { ...actionConfig },
      triggerEvent: actionConfig.triggerEvent || 'onClick'
    };

    let updatedActions;
    
    if (editingIndex !== null) {
      // Update existing action
      updatedActions = [...actions];
      updatedActions[editingIndex] = newAction;
      setEditingIndex(null);
    } else {
      // Add new action
      updatedActions = [...actions, newAction];
    }

    // Update component
    onUpdate(component.id, { actions: updatedActions });
    setActions(updatedActions);
    setShowActionPopup(false);
  };

  /**
   * Handle editing an action
   */
  const handleEditAction = (index) => {
    setEditingIndex(index);
    setShowActionPopup(true);
  };

  /**
   * Handle deleting an action
   */
  const handleDeleteAction = (index) => {
    const confirmed = window.confirm('Are you sure you want to remove this action?');
    if (!confirmed) return;

    const updatedActions = actions.filter((_, i) => i !== index);
    onUpdate(component.id, { actions: updatedActions });
    setActions(updatedActions);
  };

  /**
   * Handle testing an action
   */
  const handleTestAction = (action) => {
    console.log('Testing action:', action);
    executeAction(action.actionJSON);
  };

  return (
    <div style={{ marginTop: '16px' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '12px'
      }}>
        <label style={{
          fontSize: '12px',
          fontWeight: '600',
          color: '#374151'
        }}>
          Actions ({actions.length})
        </label>
        <button
          type="button"
          onClick={() => {
            setEditingIndex(null);
            setShowActionPopup(true);
          }}
          style={{
            padding: '6px 12px',
            fontSize: '12px',
            fontWeight: '600',
            color: '#FFFFFF',
            backgroundColor: '#8B5CF6',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          ➕ Add Action
        </button>
      </div>

      {/* Existing Actions List */}
      {actions.length > 0 && (
        <div style={{ marginBottom: '12px' }}>
          {actions.map((action, index) => (
            <div
              key={index}
              style={{
                padding: '12px',
                background: '#F9FAFB',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                marginBottom: '8px'
              }}
            >
              {/* Action Header */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '8px'
              }}>
                <div>
                  <div style={{
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#111827',
                    marginBottom: '4px'
                  }}>
                    {action.actionJSON?.name || 'Unnamed Action'}
                  </div>
                  <div style={{
                    fontSize: '11px',
                    color: '#6B7280'
                  }}>
                    {action.actionJSON?.actionType?.replace(/([A-Z])/g, ' $1').trim()} • {action.triggerEvent}
                  </div>
                </div>
                
                {/* Action Buttons */}
                <div style={{ display: 'flex', gap: '4px' }}>
                  <button
                    type="button"
                    onClick={() => handleTestAction(action)}
                    style={{
                      padding: '4px 8px',
                      fontSize: '11px',
                      background: '#8B5CF6',
                      color: '#FFFFFF',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                    title="Test Action"
                  >
                    ▶️
                  </button>
                  <button
                    type="button"
                    onClick={() => handleEditAction(index)}
                    style={{
                      padding: '4px 8px',
                      fontSize: '11px',
                      background: 'transparent',
                      border: '1px solid #D1D5DB',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      color: '#6B7280'
                    }}
                    title="Edit"
                  >
                    ✏️
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeleteAction(index)}
                    style={{
                      padding: '4px 8px',
                      fontSize: '11px',
                      background: 'transparent',
                      border: '1px solid #FCA5A5',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      color: '#DC2626'
                    }}
                    title="Delete"
                  >
                    🗑️
                  </button>
                </div>
              </div>

              {/* Action Preview */}
              <div style={{
                backgroundColor: '#FFFFFF',
                padding: '12px',
                borderRadius: '6px',
                border: '1px solid #E5E7EB',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '60px'
              }}>
                <ActionElementRenderer
                  actionConfig={action.actionJSON}
                  isLive={false}
                  onExecute={null}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {actions.length === 0 && (
        <div style={{
          padding: '20px',
          textAlign: 'center',
          backgroundColor: '#F9FAFB',
          borderRadius: '8px',
          border: '2px dashed #E5E7EB'
        }}>
          <div style={{ fontSize: '32px', marginBottom: '8px' }}>🎬</div>
          <div style={{ fontSize: '13px', color: '#6B7280', marginBottom: '4px' }}>
            No actions configured
          </div>
          <div style={{ fontSize: '11px', color: '#9CA3AF' }}>
            Click "Add Action" to configure interactive behavior
          </div>
        </div>
      )}

      {/* Action Configuration Popup */}
      {showActionPopup && (
        <ActionConfigPopup
          isOpen={showActionPopup}
          onClose={() => {
            setShowActionPopup(false);
            setEditingIndex(null);
          }}
          onActionSelect={handleActionSelect}
        />
      )}

      {/* Help Text */}
      <div style={{
        marginTop: '8px',
        fontSize: '11px',
        color: '#9CA3AF',
        fontStyle: 'italic'
      }}>
        Actions define what happens when users interact with this component
      </div>
    </div>
  );
};

export default ComponentActionsForm;
